import { useQuery } from 'react-query';
import {
  IS_ZAPFEED_ENABLED,
  ZAPFEED_API_KEY,
  ZAPFEED_URL
} from '@utils/constants/app-constants';
import {
  ZapfeedPost,
  ZapfeedPostStatus,
  ZapfeedPostsResponse,
  ZAPFEED_OPEN_STATUSES
} from '@utils/types/back-office/zapfeed';

/** How many posts we pull to build the widget's counts and recent list. */
const PAGE_SIZE = 100;

export interface ZapfeedSummary {
  posts: ZapfeedPost[];
  openPosts: ZapfeedPost[];
  statusCounts: Record<ZapfeedPostStatus, number>;
  openCount: number;
  bugCount: number;
  total: number;
}

const emptyStatusCounts = (): Record<ZapfeedPostStatus, number> => ({
  NEW: 0,
  OPEN: 0,
  UNDER_REVIEW: 0,
  PLANNED: 0,
  IN_PROGRESS: 0,
  COMPLETED: 0,
  DECLINED: 0,
  CLOSED: 0
});

const summarize = (posts: ZapfeedPost[]): ZapfeedSummary => {
  const statusCounts = emptyStatusCounts();
  for (const post of posts) {
    if (post.status in statusCounts) {
      statusCounts[post.status] += 1;
    }
  }

  const openPosts = posts.filter(p => ZAPFEED_OPEN_STATUSES.includes(p.status));

  return {
    posts,
    openPosts,
    statusCounts,
    openCount: openPosts.length,
    bugCount: openPosts.filter(p => p.type === 'BUG').length,
    total: posts.length
  };
};

/**
 * Reads the Scriptix board from Zapfeed's integrations API.
 *
 * Deliberately uses `fetch` instead of the shared Axios instance: that one
 * injects the Scriptix bearer token and logs the user out on any 401, which
 * would turn an expired Zapfeed key into a forced logout.
 *
 * Failures stay silent (no toast) — the Bug Board is a side panel on the
 * dashboard and a third-party outage shouldn't shout at the operator.
 */
export const useGetZapfeedPosts = () => {
  const fetchPosts = async (): Promise<ZapfeedSummary> => {
    const response = await fetch(
      `${ZAPFEED_URL}/api/integrations/posts?limit=${PAGE_SIZE}&sort=newest`,
      {
        headers: {
          Authorization: `Bearer ${ZAPFEED_API_KEY}`,
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Zapfeed request failed (${response.status})`);
    }

    const data = (await response.json()) as ZapfeedPostsResponse;
    return summarize(data?.posts ?? []);
  };

  return useQuery<ZapfeedSummary, Error>(['zapfeed', 'posts'], fetchPosts, {
    enabled: IS_ZAPFEED_ENABLED,
    staleTime: 60_000,
    cacheTime: 10 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    useErrorBoundary: false
  });
};
